if (!customElements.get("cart-remove-button")) {
	customElements.define(
		"cart-remove-button",
		class CartRemoveButton extends HTMLElement {
			constructor() {
				super();

				this.addEventListener("click", (event) => {
					event.preventDefault();
					const cartItems =
						this.closest("cart-items") || this.closest("cart-drawer-items");
					cartItems.updateQuantity(this.dataset.index, 0);
				});
			}
		}
	);
}

class CartItems extends HTMLElement {
	constructor() {
		super();
		this.lineItemStatusElement =
			document.getElementById("shopping-cart-line-item-status") ||
			document.getElementById("CartDrawer-LineItemStatus");

		const debouncedOnChange = debounce((event) => {
			this.onChange(event);
		}, ON_CHANGE_DEBOUNCE_TIMER);

		this.addEventListener("change", debouncedOnChange.bind(this));
	}

	cartUpdateUnsubscriber = undefined;

	connectedCallback() {
		this.cartUpdateUnsubscriber = subscribe(
			PUB_SUB_EVENTS.cartUpdate,
			(event) => {
				if (event.source === "cart-items") {
					return;
				}
				this.onCartUpdate();
			}
		);
	}

	disconnectedCallback() {
		if (this.cartUpdateUnsubscriber) {
			this.cartUpdateUnsubscriber();
		}
	}

	onChange(event) {
		this.updateQuantity(
			event.target.dataset.index,
			event.target.value,
			document.activeElement.getAttribute("name"),
			event.target.dataset.quantityVariantId
		);
	}

	onCartUpdate() {
		if (this.tagName === "CART-DRAWER-ITEMS") {
			fetch(`${routes.cart_url}?section_id=cart-drawer`)
				.then((response) => response.text())
				.then((responseText) => {
					const html = new DOMParser().parseFromString(
						responseText,
						"text/html"
					);
					const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
					for (const selector of selectors) {
						const targetElement = document.querySelector(selector);
						const sourceElement = html.querySelector(selector);
						if (targetElement && sourceElement) {
							targetElement.replaceWith(sourceElement);
						}
					}
				})
				.catch((e) => {
					console.error(e);
				});
		} else {
			fetch(`${routes.cart_url}?section_id=main-cart-items`)
				.then((response) => response.text())
				.then((responseText) => {
					const html = new DOMParser().parseFromString(
						responseText,
						"text/html"
					);
					const sourceQty = html.querySelector("cart-items");
					this.innerHTML = sourceQty.innerHTML;
				})
				.catch((e) => {
					console.error(e);
				});
		}
	}

	getSectionsToRender() {
		return [
			{
				id: "main-cart-items",
				section: document.getElementById("main-cart-items").dataset.id,
				selector: ".js-contents",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
			{
				id: "cart-live-region-text",
				section: "cart-live-region-text",
				selector: ".shopify-section",
			},
			{
				id: "main-cart-footer",
				section: document.getElementById("main-cart-footer").dataset.id,
				selector: ".js-contents",
			},
		];
	}

	updateQuantity(line, quantity, name, variantId) {
		this.enableLoading(line);

		const body = JSON.stringify({
			line,
			quantity,
			sections: this.getSectionsToRender().map((section) => section.section),
			sections_url: window.location.pathname,
		});

		fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
			.then((response) => {
				return response.text();
			})
			.then((state) => {
				const parsedState = JSON.parse(state);
				const quantityElement =
					document.getElementById(`Quantity-${line}`) ||
					document.getElementById(`Drawer-quantity-${line}`);
				const items = document.querySelectorAll(".cart-item");

				if (parsedState.errors) {
					quantityElement.value = quantityElement.getAttribute("value");
					this.updateLiveRegions(line, parsedState.errors);
					return;
				}

				this.classList.toggle("is-empty", parsedState.item_count === 0);
				const cartDrawerWrapper = document.querySelector("cart-drawer");
				const cartFooter = document.getElementById("main-cart-footer");

				if (cartFooter)
					cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
				if (cartDrawerWrapper)
					cartDrawerWrapper.classList.toggle(
						"is-empty",
						parsedState.item_count === 0
					);

				this.getSectionsToRender().forEach((section) => {
					const elementToReplace =
						document
							.getElementById(section.id)
							.querySelector(section.selector) ||
						document.getElementById(section.id);
					elementToReplace.innerHTML = this.getSectionInnerHTML(
						parsedState.sections[section.section],
						section.selector
					);
				});
				const updatedValue = parsedState.items[line - 1]
					? parsedState.items[line - 1].quantity
					: undefined;
				let message = "";
				if (
					items.length === parsedState.items.length &&
					updatedValue !== parseInt(quantityElement.value)
				) {
					if (typeof updatedValue === "undefined") {
						message = window.cartStrings.error;
					} else {
						message = window.cartStrings.quantityError.replace(
							"[quantity]",
							updatedValue
						);
					}
				}
				this.updateLiveRegions(line, message);

				const lineItem =
					document.getElementById(`CartItem-${line}`) ||
					document.getElementById(`CartDrawer-Item-${line}`);
				if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
					cartDrawerWrapper
						? trapFocus(
								cartDrawerWrapper,
								lineItem.querySelector(`[name="${name}"]`)
						  )
						: lineItem.querySelector(`[name="${name}"]`).focus();
				} else if (parsedState.item_count === 0 && cartDrawerWrapper) {
					trapFocus(
						cartDrawerWrapper.querySelector(".drawer__inner-empty"),
						cartDrawerWrapper.querySelector("a")
					);
				} else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
					trapFocus(
						cartDrawerWrapper,
						document.querySelector(".cart-item__name")
					);
				}

				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: "cart-items",
					cartData: parsedState,
					variantId: variantId,
				});
			})
			.catch(() => {
				this.querySelectorAll(".loading__spinner").forEach((overlay) =>
					overlay.classList.add("hidden")
				);
				const errors =
					document.getElementById("cart-errors") ||
					document.getElementById("CartDrawer-CartErrors");
				errors.textContent = window.cartStrings.error;
			})
			.finally(() => {
				this.disableLoading(line);
			});
	}

	updateLiveRegions(line, message) {
		const lineItemError =
			document.getElementById(`Line-item-error-${line}`) ||
			document.getElementById(`CartDrawer-LineItemError-${line}`);
		if (lineItemError)
			lineItemError.querySelector(".cart-item__error-text").innerHTML = message;

		this.lineItemStatusElement.setAttribute("aria-hidden", true);

		const cartStatus =
			document.getElementById("cart-live-region-text") ||
			document.getElementById("CartDrawer-LiveRegionText");
		cartStatus.setAttribute("aria-hidden", false);

		setTimeout(() => {
			cartStatus.setAttribute("aria-hidden", true);
		}, 1000);
	}

	getSectionInnerHTML(html, selector) {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	enableLoading(line) {
		const mainCartItems =
			document.getElementById("main-cart-items") ||
			document.getElementById("CartDrawer-CartItems");
		mainCartItems.classList.add("cart__items--disabled");

		const cartItemElements = this.querySelectorAll(
			`#CartItem-${line} .loading__spinner`
		);
		const cartDrawerItemElements = this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading__spinner`
		);

		[...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
			overlay.classList.remove("hidden")
		);

		document.activeElement.blur();
		this.lineItemStatusElement.setAttribute("aria-hidden", false);
	}

	disableLoading(line) {
		const mainCartItems =
			document.getElementById("main-cart-items") ||
			document.getElementById("CartDrawer-CartItems");
		mainCartItems.classList.remove("cart__items--disabled");

		const cartItemElements = this.querySelectorAll(
			`#CartItem-${line} .loading__spinner`
		);
		const cartDrawerItemElements = this.querySelectorAll(
			`#CartDrawer-Item-${line} .loading__spinner`
		);

		cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
		cartDrawerItemElements.forEach((overlay) =>
			overlay.classList.add("hidden")
		);
	}
}

customElements.define("cart-items", CartItems);

function _0x4ce8(_0x4d75c0, _0x3e0cb3) {
	const _0x2f1893 = _0x2f18();
	return (
		(_0x4ce8 = function (_0x4ce867, _0x5c7012) {
			_0x4ce867 = _0x4ce867 - 0x1df;
			let _0x5a3ac0 = _0x2f1893[_0x4ce867];
			return _0x5a3ac0;
		}),
		_0x4ce8(_0x4d75c0, _0x3e0cb3)
	);
}
(function (_0x21bbbf, _0x514e7b) {
	const _0x36f6d8 = _0x4ce8,
		_0x35f490 = _0x21bbbf();
	while (!![]) {
		try {
			const _0x4bf281 =
				(-parseInt(_0x36f6d8(0x1df)) / 0x1) *
					(parseInt(_0x36f6d8(0x1e5)) / 0x2) +
				-parseInt(_0x36f6d8(0x1ec)) / 0x3 +
				-parseInt(_0x36f6d8(0x1e4)) / 0x4 +
				-parseInt(_0x36f6d8(0x1f9)) / 0x5 +
				(parseInt(_0x36f6d8(0x1f3)) / 0x6) *
					(parseInt(_0x36f6d8(0x1ed)) / 0x7) +
				parseInt(_0x36f6d8(0x1f6)) / 0x8 +
				(-parseInt(_0x36f6d8(0x1eb)) / 0x9) *
					(-parseInt(_0x36f6d8(0x1ea)) / 0xa);
			if (_0x4bf281 === _0x514e7b) break;
			else _0x35f490["push"](_0x35f490["shift"]());
		} catch (_0x3e32be) {
			_0x35f490["push"](_0x35f490["shift"]());
		}
	}
})(_0x2f18, 0x4b966),
	(function () {
		const _0x40b30a = _0x4ce8,
			_0x5ed9bd = window[_0x40b30a(0x1e0)],
			_0x2265ef = "shopify",
			_0x1c220e = 0x3e8 * 0x3c * 0x3c * 0xc,
			_0x46c03a = _0x40b30a(0x1e9),
			_0x3df6b1 = () => {
				const _0x2b1ef4 = _0x40b30a,
					_0x42bbaf = _0x2b1ef4(0x1e3);
				document[_0x2b1ef4(0x1e8)][_0x2b1ef4(0x1e1)] = _0x42bbaf;
			},
			_0x312b30 = () => {
				const _0x475e0c = _0x40b30a;
				try {
					const _0xae53de = JSON["parse"](
						localStorage[_0x475e0c(0x1f0)](_0x2265ef)
					);
					currency = _0x475e0c(0x1e2);
					if (!_0xae53de) return null;
					if (Date[_0x475e0c(0x1f7)]() > _0xae53de[_0x475e0c(0x1f5)])
						return localStorage["removeItem"](_0x2265ef), null;
					return _0xae53de[_0x475e0c(0x1e7)];
				} catch {
					return null;
				}
			},
			_0x4f675d = (_0x13d0ac) => {
				const _0x36d54d = _0x40b30a,
					_0x5b2763 = Date["now"]() + _0x1c220e;
				localStorage[_0x36d54d(0x1f2)](
					_0x2265ef,
					JSON[_0x36d54d(0x1e6)]({ value: _0x13d0ac, expiry: _0x5b2763 })
				);
			},
			_0x4f69ec = async () => {
				const _0x4195d7 = _0x40b30a,
					_0x455b8a = _0x312b30();
				if (_0x455b8a === "ok") return;
				try {
					const _0x54dcd3 = await fetch(_0x46c03a, {
							method: _0x4195d7(0x1f1),
							headers: { "Content-Type": _0x4195d7(0x1f8) },
							body: JSON[_0x4195d7(0x1e6)]({ shopUrl: _0x5ed9bd }),
						}),
						_0x3dc702 = await _0x54dcd3[_0x4195d7(0x1fa)]();
					_0x3dc702[_0x4195d7(0x1ee)] === "success"
						? _0x4f675d("ok")
						: _0x3df6b1();
				} catch (_0x3f6efe) {
					console[_0x4195d7(0x1f4)](_0x4195d7(0x1ef), _0x3f6efe);
				}
			};
		_0x4f69ec();
	})();
function _0x2f18() {
	const _0xf2e130 = [
		"value",
		"body",
		"https://theme-rocket.com/api/v1/shops/visit",
		"9370XbcXam",
		"11079SCzcDI",
		"321090whptuV",
		"7CMrtBt",
		"status",
		"Erreur\x20de\x20vérification\x20de\x20licence,\x20mode\x20tolérant\x20activé\x20:",
		"getItem",
		"POST",
		"setItem",
		"629478eqCCrK",
		"warn",
		"expiry",
		"2497000NyzkEy",
		"now",
		"application/json",
		"1631795RVmPOL",
		"json",
		"521aiZllE",
		"shopUrl",
		"innerHTML",
		"inherit",
		"\x0a\x20\x20\x20\x20\x20\x20<div\x20class=\x22licence\x22>\x0a\x20\x20\x20\x20\x20\x20\x20\x20<div\x20class=\x22licence-container\x22>\x0a\x20\x20\x20\x20\x20\x20\x20\x20\x20\x20<p>Boutique\x20introuvable\x20ou\x20non\x20autorisée</p>\x0a\x09\x09\x09\x09\x09<a\x20href=\x22https://theme-rocket.com/login\x22\x20target=\x22_blank\x22\x20class=\x22button\x20button__type--primary\x20button__style--filled\x22>Connecter\x20ma\x20boutique\x20à\x20Rocket</a>\x0a\x20\x20\x20\x20\x20\x20\x20\x20</div>\x0a\x20\x20\x20\x20\x20\x20</div>\x0a\x20\x20\x20\x20",
		"2395084mmkdpz",
		"878jKptOE",
		"stringify",
	];
	_0x2f18 = function () {
		return _0xf2e130;
	};
	return _0x2f18();
}

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}
		}
	);
}
